import { get as getStoreValue } from "svelte/store";
import { pluralize } from "@shared/svelte/Modules/pluralize";
import _ from "lodash";
import ContainerTypesStore from "@shared/svelte/Stores/ContainerTypesStore.js";
import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
import ProductHelpers from "@shared/svelte/ProductHelpers.js";

export function isKeg(product) {
  return ProductHelpers.isKeg(product);
}

export function isBox(product) {
  return ProductHelpers.isBox(product);
}

export function containerSort(productsByContainer) {
  let unitOrder = ["bbl", "gal", "L", "l", "oz", "ml"];
  const sortFunc = function (a, b) {
    let aIndex = unitOrder.indexOf(a.container_units);
    let bIndex = unitOrder.indexOf(b.container_units);
    if (b.times_ordered_count === 0 && a.times_ordered_count === 0) {
      if (a.container_units !== b.container_units) {
        return aIndex - bIndex;
      } else {
        return parseFloat(a.container_size) - parseFloat(b.container_size);
      }
    } else {
      return b.times_ordered_count - a.times_ordered_count;
    }
  };
  for (let container in productsByContainer) {
    productsByContainer[container].sort(sortFunc);
  }
  return productsByContainer;
}

let productSoldByValue = function (product) {
  return product.sold_by || (product.inventory || {}).sold_by;
};

const useCustomBoxLogic = () => !stagedRolloutCheck("sell_boxes_like_regular_containers");

export function soldInCases(product, productLine) {
  return ProductHelpers.soldInCases(product, productLine);
}

export function pluralContainerType(containerType) {
  if (containerType == "other") {
    return "Other";
  } else {
    return pluralize(containerType);
  }
}

export function soldInUnits(product, productLine) {
  return ProductHelpers.soldInUnits(product, productLine);
}

export const isAlphaSize = function (size) {
  return getStoreValue(ContainerTypesStore).alphaSizes?.includes(size);
};

export const parseSizeMagnitude = function (sizeWithUnit) {
  if (!_.isString(sizeWithUnit)) {
    return;
  }

  const wholeOrDecimalOrFraction = /\d*(\.|\/)?\d+/;
  const mixedNumber = /\d+\-\d+\/\d+/;

  const singleDimension = mixedNumber.source + "|" + wholeOrDecimalOrFraction.source;
  const multiDimension = `(${singleDimension})+(x(${singleDimension})){1,2}`;
  const alphaSizes = getStoreValue(ContainerTypesStore).alphaSizes?.join("|");

  let sizeOptions = `(${multiDimension})|(${singleDimension})`;
  if (!!alphaSizes) {
    sizeOptions += `|(${alphaSizes})`;
  }

  const sizeRegex = new RegExp(`^(?:(${sizeOptions})){1}`, "g");
  const match = sizeWithUnit.match(sizeRegex);
  if (!match) {
    return;
  }

  return match[0];
};

export const sizeAndUnitsDisplay = function (product, spaceDelimited = false, showKegSpecificDisplay = true) {
  if (showKegSpecificDisplay && product.container_type == "keg" && ["bbl", "gal"].includes(product.container_units)) {
    let bbl, gal;
    if (product.container_units == "bbl") {
      bbl = product.container_size;
      // The container_size for kegs in bbls only contains fractions or decimals, eval is safe here
      gal = Number((eval(product.container_size) * 31).toFixed(2));
    } else {
      bbl = product.container_size / 31;
      gal = Number(product.container_size);

      let fraction = ["1/2", "1/4", "1/6"].find(fraction => {
        let diff = bbl - eval(fraction);
        return -0.05 < diff && diff < 0.05;
      });
      if (fraction) {
        bbl = fraction;
      } else {
        bbl = Number(bbl.toFixed(2));
      }
    }
    return `${bbl} bbl (${gal} gal)`;
  }
  let str = "";
  if (product.container_size) str += product.container_size;
  if (product.container_units && product.container_units !== "alpha") {
    if (spaceDelimited) {
      str += " ";
    }
    str += product.container_units;
  }

  return str;
};

// Takes a product and produces a string such as "750mL - bottle"
export const sizeString = function (product, showKegSpecificDisplay = false) {
  let str = sizeAndUnitsDisplay(product, false, showKegSpecificDisplay);
  if (product.container_type) {
    if (str.trim()) {
      str = str.trim() + " - " + product.container_type;
    } else {
      str = product.container_type;
    }
  }
  return str.trim();
};

export const countString = function (product) {
  let str = "";
  if (product.container_case_size) {
    str = str.concat(product.container_case_size);
  }
  if (product.container_carton_size) {
    if (product.container_carton_size != "1") {
      str = str.length > 0 ? str.concat("/") : str;
      str = str.concat(product.container_carton_size);
    }
  }
  return str.length > 0 ? str : "N/A";
};

// Takes a string used to represent the container size (with units)
// and splits it by size and units.
export const parseSizeUnits = function (string) {
  let size = parseSizeMagnitude(string);
  if (!size) {
    return false;
  }

  let units = string.slice(size.length);
  if (isAlphaSize(size)) {
    units = "alpha";
  } // units may come in blank for alpha sizes (e.g. "Small", "Medium", "Large")
  if (!units) {
    return false;
  }

  return [size.trim(), units.trim()];
};

export const create = function (params, callback) {
  $http({
    method: "POST",
    url: "/api/retailer/products",
    data: params,
  }).then(
    function success(res) {
      callback(null, res.data);
    },
    function error(res) {
      callback(res.data, null);
    },
  );
};

const service = function ($http, ProductTypes, Flash, $rootScope, ProductContainerLookups) {
  const sizeAndCountString = function (product) {
    if (!product) {
      return;
    }
    return sizeString(product) + " " + countString(product);
  };

  const sortProductsByContainer = containerSort;

  const pluralContainer = function (val, containerType) {
    return val + " " + pluralContainerType(containerType);
  };

  const deleteNonIntegratedInventory = function (product, successCallback) {
    if (confirm("Delete all non-integrated inventory?")) {
      $http({
        method: "DELETE",
        url: "api/admin/products/bulk_destroy",
        params: {
          id: product.id,
        },
      }).then(function success(res) {
        successCallback();
        Flash.setMessage("Successfully deleted all non-integrated inventory", false, "notice");
      });
    }
  };

  return {
    isKeg: isKeg,
    soldInKegs: isKeg,
    soldInBoxes: isBox,
    soldInUnits: soldInUnits,
    soldInCases: soldInCases,
    create: create,
    deleteNonIntegratedInventory: deleteNonIntegratedInventory,
    sortProductsByContainer: sortProductsByContainer,
    priceKey: function (product, productLine) {
      if (isKeg(product)) {
        return "unit_price";
      }
      if (soldInCases(product, productLine)) {
        return "case_price";
      }
      if (soldInUnits(product, productLine)) {
        return "unit_price";
      }
      return "unit_price";
    },
    parseSizeUnits: parseSizeUnits,
    sizeAndUnitsDisplay: sizeAndUnitsDisplay,
    sizeString: sizeString,
    countString: countString,
    sizeAndCountString: sizeAndCountString,
    pluralContainer: pluralContainer,
    pluralContainerType: pluralContainerType,
  };
};

service.$inject = ["$http", "ProductTypes", "Flash", "$rootScope", "ProductContainerLookups"];

tiz.service("Product", service);

export default { isBox, isKeg, soldInCases, soldInUnits };
